'use client';

import type { ComponentType, SVGProps } from 'react';

import {
  Amex,
  ApplePay,
  Diners,
  Discover,
  GenericCard,
  GooglePay,
  Jcb,
  Maestro,
  Mastercard,
  PayPal,
  Sepa,
  StripeLink,
  UnionPay,
  Visa,
} from '@/icons/brands';
import { mergeClassNames } from '@/lib/utils';
import { captureError } from '@/lib/capture-error';

type BrandIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const BRAND_MAP: Record<string, { Icon: BrandIconComponent; label: string }> = {
  visa: { Icon: Visa, label: 'Visa card' },
  mastercard: { Icon: Mastercard, label: 'Mastercard card' },
  amex: { Icon: Amex, label: 'American Express card' },
  discover: { Icon: Discover, label: 'Discover card' },
  diners: { Icon: Diners, label: 'Diners Club card' },
  jcb: { Icon: Jcb, label: 'JCB card' },
  unionpay: { Icon: UnionPay, label: 'UnionPay card' },
  link: { Icon: StripeLink, label: 'Stripe Link' },
  maestro: { Icon: Maestro, label: 'Maestro card' },
};

const reportedUnmapped = new Set<string>();

const reportUnmappedBrand = (
  unmappedValue: string,
  ctx: {
    brand: string | null;
    displayBrand?: string | null;
    walletType?: string | null;
    type?: string | null;
  }
) => {
  if (reportedUnmapped.has(unmappedValue)) return;
  reportedUnmapped.add(unmappedValue);
  captureError({
    error: new Error(`Unmapped payment brand: ${unmappedValue}`),
    source: 'ui_crash',
    severity: 'warning',
    context: {
      brand: ctx.brand,
      display_brand: ctx.displayBrand,
      wallet_type: ctx.walletType,
      payment_method_type: ctx.type,
    },
  });
};

interface CardBrandIconProps {
  brand: string | null;
  displayBrand?: string | null;
  walletType?: string | null;
  type?: string | null;
  size?: 'sm' | 'md';
  className?: string;
}

export const CardBrandIcon = ({
  brand,
  displayBrand,
  walletType,
  type,
  size = 'md',
  className,
}: CardBrandIconProps) => {
  const { Icon, label } = resolveBrand({ brand, displayBrand, walletType, type });
  const height = size === 'sm' ? 28 : 40;

  return (
    <span
      role="img"
      aria-label={label}
      className={mergeClassNames('inline-flex items-center', className)}
    >
      <Icon height={height} />
    </span>
  );
};

function resolveBrand({
  brand,
  displayBrand,
  walletType,
  type,
}: {
  brand: string | null;
  displayBrand?: string | null;
  walletType?: string | null;
  type?: string | null;
}): { Icon: BrandIconComponent; label: string } {
  if (type === 'sepa_debit') {
    return { Icon: Sepa, label: 'SEPA Direct Debit' };
  }

  if (type === 'paypal') {
    return { Icon: PayPal, label: 'PayPal' };
  }

  if (walletType === 'apple_pay') {
    return { Icon: ApplePay, label: 'Apple Pay' };
  }

  if (walletType === 'google_pay') {
    return { Icon: GooglePay, label: 'Google Pay' };
  }

  if (walletType === 'link') {
    return { Icon: StripeLink, label: 'Stripe Link' };
  }

  if (walletType) {
    reportUnmappedBrand(`wallet:${walletType}`, {
      brand,
      displayBrand,
      walletType,
      type,
    });
  }

  const key = brand?.toLowerCase();
  if (!key || key === 'unknown') {
    reportUnmappedBrand(`brand:${key ?? 'null'}`, {
      brand,
      displayBrand,
      walletType,
      type,
    });
    return { Icon: GenericCard, label: 'Card' };
  }

  const mapped = BRAND_MAP[key];
  if (!mapped) {
    reportUnmappedBrand(`brand:${key}`, { brand, displayBrand, walletType, type });
    return { Icon: GenericCard, label: 'Card' };
  }

  return { Icon: mapped.Icon, label: mapped.label };
}
